/**
 * src/lib/offline/db.js — raw IndexedDB wrapper for the `acn-offline` database.
 *
 * Mirrors the proven pattern in src/lib/voiceQueue.js (no external deps, one
 * shared connection promise). This DB is disjoint from `acn-voice-queue` and
 * `acn-resumable-uploads` — it backs ONLY the offline tasks cache + outbox.
 *
 * Stores:
 *   - tasks_cache (keyPath 'id')          — write-through mirror of fetched tasks
 *   - outbox      (keyPath 'local_id', autoIncrement; idx status, created_at)
 *   - meta        (keyPath 'key')         — small kv: last_synced_at, etc.
 */

const DB_NAME = 'acn-offline';
const DB_VERSION = 2;

export const STORES = {
  // Single domain-agnostic cache for every collection (tasks, projects, crm,
  // vessels, ...). Records are wrappers: { cache_key:'<domain>::<id>', domain, row }.
  // A `domain` index supports read-by-collection + replace-by-collection.
  entities: 'entities',
  outbox: 'outbox',
  meta: 'meta',
};

let dbPromise = null;

// TICKET-1099: iOS WebKit can wedge its IndexedDB server ("An internal error was
// encountered in the Indexed Database server") so open()/transactions never fire
// a callback. Callers await these on the data-load path (cache write-through), so
// an un-settled promise froze useAppData.reload() on an infinite spinner. Every
// op is bounded: a wedged IDB degrades to "no cache", never a hung app.
const IDB_TIMEOUT_MS = 4000;

function bounded(promise, label) {
  // TICKET-1104: last IDB op, read by the stuck-loading breadcrumb so a WebKit
  // 'internal error' can be tied to what we were doing when the process died.
  globalThis.__acnLastIdbOp = `${label}@${new Date().toISOString()}`;
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(`IndexedDB ${label} timed out`)), IDB_TIMEOUT_MS);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/** Open (or create) the database. Reuses one connection promise. */
export function openDB() {
  if (dbPromise) return dbPromise;
  const opening = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      // v2: drop the v1 task-only store in favour of the generic entities store.
      if (db.objectStoreNames.contains('tasks_cache')) {
        db.deleteObjectStore('tasks_cache');
      }
      if (!db.objectStoreNames.contains(STORES.entities)) {
        const entities = db.createObjectStore(STORES.entities, { keyPath: 'cache_key' });
        entities.createIndex('domain', 'domain', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.outbox)) {
        const outbox = db.createObjectStore(STORES.outbox, {
          keyPath: 'local_id',
          autoIncrement: true,
        });
        outbox.createIndex('status', 'status', { unique: false });
        outbox.createIndex('created_at', 'created_at', { unique: false });
        outbox.createIndex('domain', 'domain', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.meta)) {
        db.createObjectStore(STORES.meta, { keyPath: 'key' });
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      // TICKET-664: the browser can force-close this connection out from under us
      // (observed on WebKit backgrounding — "The database connection is closing").
      // Without this, dbPromise keeps resolving to the dead connection forever,
      // so EVERY subsequent read/write throws the same error until a full page
      // reload. Drop the cached promise so the next call reopens fresh.
      db.onclose = () => { dbPromise = null; };
      resolve(db);
    };
    request.onerror = () => reject(request.error);
  });
  // A failed/timed-out open must not be cached forever — next call retries fresh.
  const p = bounded(opening, 'open').catch((err) => {
    if (dbPromise === p) dbPromise = null;
    throw err;
  });
  dbPromise = p;
  return p;
}

/**
 * Read all entity wrappers for one domain via the `domain` index.
 * Returns the unwrapped `row` objects (cache metadata stripped).
 */
export function idbReadDomain(domain) {
  return openDB().then(
    (db) =>
      bounded(new Promise((resolve, reject) => {
        const tx = db.transaction(STORES.entities, 'readonly');
        const idx = tx.objectStore(STORES.entities).index('domain');
        const req = idx.getAll(IDBKeyRange.only(domain));
        req.onsuccess = () => resolve((req.result || []).map((w) => w.row));
        req.onerror = () => reject(req.error);
        tx.onabort = () => reject(tx.error);
      }), 'read'),
  );
}

/**
 * Atomically replace every row of one domain: delete the domain's existing
 * keys (via the index) then write the new wrappers — all in one transaction.
 * Other domains in the store are untouched.
 */
export function idbReplaceDomain(domain, rows) {
  const list = Array.isArray(rows) ? rows.filter((r) => r && r.id != null) : [];
  return openDB().then(
    (db) =>
      bounded(new Promise((resolve, reject) => {
        const tx = db.transaction(STORES.entities, 'readwrite');
        const os = tx.objectStore(STORES.entities);
        const idx = os.index('domain');
        const cursorReq = idx.openKeyCursor(IDBKeyRange.only(domain));
        cursorReq.onsuccess = (e) => {
          const cur = e.target.result;
          if (cur) {
            os.delete(cur.primaryKey);
            cur.continue();
          } else {
            for (const row of list) {
              os.put({ cache_key: `${domain}::${row.id}`, domain, row });
            }
          }
        };
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      }), 'replaceDomain'),
  );
}

/** Clear one domain's rows (leaves other domains intact). */
export function idbClearDomain(domain) {
  return idbReplaceDomain(domain, []);
}

/** Reset the cached connection promise (tests). */
export function _resetDB() {
  dbPromise = null;
}

/** Close the connection and reset (tests). */
export async function _closeDB() {
  if (dbPromise) {
    try {
      const db = await dbPromise;
      db.close();
    } catch { /* db may not have opened */ }
  }
  dbPromise = null;
}

// ── Generic store helpers ────────────────────────────────────────────────
function run(store, mode, fn) {
  return openDB().then(
    (db) =>
      bounded(new Promise((resolve, reject) => {
        const tx = db.transaction(store, mode);
        const os = tx.objectStore(store);
        const req = fn(os);
        // TICKET-995: resolve only when the TRANSACTION commits. A request can succeed and
        // its transaction still abort afterwards (quota, a later failing request), which
        // rolls the write back; resolving on req.onsuccess reported that lost write as saved.
        let result;
        req.onsuccess = () => { result = req.result; };
        tx.oncomplete = () => resolve(result);
        const fail = (e) => reject(e?.target?.error || tx.error || req.error || new Error(`IndexedDB transaction aborted (${store})`));
        tx.onerror = fail; // a failing request's error bubbles here first (event.target = that request)
        tx.onabort = fail;
      }), store),
  );
}

/** Test hook: the generic transaction runner. */
export const _run = (store, mode, fn) => run(store, mode, fn);

/** put — insert or replace (store must have an inline keyPath). */
export function idbPut(store, value) {
  return run(store, 'readwrite', (os) => os.put(value));
}

/** add — insert; returns the generated key (for autoIncrement stores). */
export function idbAdd(store, value) {
  return run(store, 'readwrite', (os) => os.add(value));
}

/** get — by key; resolves undefined when absent. */
export function idbGet(store, key) {
  return run(store, 'readonly', (os) => os.get(key));
}

/** getAll — every record in the store. */
export function idbGetAll(store) {
  return run(store, 'readonly', (os) => os.getAll());
}

/**
 * TICKET-1105 — KEYS-ONLY reads. On iOS the IndexedDB server lives in WebKit's
 * network process; materialising multi-MB ArrayBuffers (queued photos) there
 * crashed it, killing IDB AND every fetch. These never load a record value.
 */
/** count of index entries equal to each of `values` (summed). */
export function idbIndexCount(store, index, values) {
  const list = Array.isArray(values) ? values : [values];
  return openDB().then((db) => bounded(new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const idx = tx.objectStore(store).index(index);
    let total = 0;
    for (const v of list) {
      const req = idx.count(IDBKeyRange.only(v));
      req.onsuccess = () => { total += req.result || 0; };
    }
    tx.oncomplete = () => resolve(total);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  }), `${store}.count`));
}

/**
 * Primary keys whose `filterIndex` value is in `filterValues`, ordered by
 * `orderIndex` (then primary key) — via getAllKeys + a KEY cursor, no values.
 */
export function idbIndexKeysOrdered(store, orderIndex, filterIndex, filterValues) {
  return openDB().then((db) => bounded(new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const os = tx.objectStore(store);
    const wanted = new Set();
    for (const v of filterValues) {
      const r = os.index(filterIndex).getAllKeys(IDBKeyRange.only(v));
      r.onsuccess = () => { for (const k of r.result || []) wanted.add(k); };
    }
    const out = [];
    const cur = os.index(orderIndex).openKeyCursor();
    cur.onsuccess = () => {
      const c = cur.result;
      if (!c) return;
      out.push(c.primaryKey);
      c.continue();
    };
    // getAllKeys requests were queued first, so `wanted` is complete by commit.
    tx.oncomplete = () => resolve(out.filter((k) => wanted.has(k)));
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  }), `${store}.keys`));
}

/** delete — by key. */
export function idbDelete(store, key) {
  return run(store, 'readwrite', (os) => os.delete(key));
}

/** clear — empty the store. */
export function idbClear(store) {
  return run(store, 'readwrite', (os) => os.clear());
}

/**
 * replaceAll — clear the store and write `values` in a SINGLE transaction.
 * Atomic: a crash mid-write leaves the prior contents intact (the whole tx
 * aborts), never a half-populated cache.
 */
export function idbReplaceAll(store, values) {
  const rows = Array.isArray(values) ? values : [];
  return openDB().then(
    (db) =>
      bounded(new Promise((resolve, reject) => {
        const tx = db.transaction(store, 'readwrite');
        const os = tx.objectStore(store);
        os.clear();
        for (const v of rows) os.put(v);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      }), store),
  );
}
